/**
 * Helper para verificar se uma conversa deve ser removida da lista do usuário atual
 * baseado em regras de atribuição de agente e times privados
 */

const LOG_PREFIX = '[REMOVAL_HELPER]';

const logRemovalDecision = (context, conversationId, shouldRemove, details = '') => {
  const action = shouldRemove ? 'REMOVE' : 'KEEP';

  if (details) {
    console.debug(`${LOG_PREFIX} [${context}] Conversation #${conversationId}: ${action} - ${details}`);
    return;
  }

  console.debug(`${LOG_PREFIX} [${context}] Conversation #${conversationId}: ${action}`);
};

/**
 * Verifica se a conversa deve ser removida para perfil VENDEDOR
 *
 * Regra: Perfil Vendedor (seller)
 * - Gatilho: Usuário atribuído alterado
 * - Se usuário atribuído é diferente de null
 * - Se o usuário atribuído é diferente do usuário logado
 * -> Remover a conversa da lista
 *
 * @param {Object} conversation - Objeto da conversa
 * @param {Object} currentUser - Usuário logado atual
 * @param {String} currentRole - Perfil/role do usuário atual
 * @returns {Boolean} - true se deve remover, false caso contrário
 */
export const shouldRemoveForSellerRole = (conversation, currentUser, currentRole) => {
  // Apenas aplica para perfil vendedor
  if (currentRole !== 'seller') {
    return false;
  }

  const assignee = conversation?.meta?.assignee;

  // Se não há assignee, não remove
  if (!assignee) {
    return false;
  }

  const shouldRemove = assignee.id !== currentUser.id;

  logRemovalDecision(
    'SELLER_RULE',
    conversation.id,
    shouldRemove,
    `assignee=#${assignee.id} currentUser=#${currentUser.id}`
  );

  return shouldRemove;
};

/**
 * Verifica se a conversa deve ser removida por estar em time privado
 *
 * Regra:
 * - Administrador sempre tem acesso
 * - Supervisor do time também tem acesso
 * - Usuário membro do time tem acesso
 * - Caso contrário, remove
 *
 * @param {Object} conversation - Objeto da conversa
 * @param {Object} currentUser - Usuário logado atual
 * @param {String} currentRole - Perfil/role do usuário atual
 * @param {Array} myTeams - Lista de times dos quais o usuário faz parte
 * @param {Array} supervisedTeamIds - IDs dos times supervisionados pelo usuário
 * @returns {Boolean} - true se deve remover, false caso contrário
 */
export const shouldRemoveForPrivateTeam = (
  conversation,
  currentUser,
  currentRole,
  myTeams,
  supervisedTeamIds = []
) => {
  // Administrador sempre possui acesso
  if (currentRole === 'administrator') {
    return false;
  }

  const team = conversation?.meta?.team;

  // Sem time atribuído
  if (!team) {
    return false;
  }

  // Time público
  if (!team.private) {
    return false;
  }

  // Usuário é membro do time
  const isUserInTeam = myTeams.some(myTeam => myTeam.id === team.id);

  // Usuário supervisiona o time
  const isSupervisorOfTeam = supervisedTeamIds.includes(team.id);

  // Remove apenas se NÃO for membro E NÃO for supervisor
  const shouldRemove = !isUserInTeam && !isSupervisorOfTeam;

  logRemovalDecision(
    'PRIVATE_TEAM_RULE',
    conversation.id,
    shouldRemove,
    `team=#${team.id} member=${isUserInTeam} supervisor=${isSupervisorOfTeam}`
  );

  return shouldRemove;
};

/**
 * Função principal que verifica se uma conversa deve ser removida
 * considerando todas as regras
 *
 * @param {Object} conversation - Objeto da conversa
 * @param {Object} store - Instância do Vuex store
 * @returns {Boolean} - true se deve remover, false caso contrário
 */
export const shouldRemoveConversation = (conversation, store) => {
  const currentUser = store.getters.getCurrentUser;
  const currentRole = store.getters.getCurrentRole;

  const myTeams = store.getters['teams/getMyTeams'];
  const supervisedTeamIds = store.getters['teams/getSupervisedTeamIds'];

  // Regra vendedor
  if (shouldRemoveForSellerRole(conversation, currentUser, currentRole)) {
    return true;
  }

  // Regra time privado
  if (
    shouldRemoveForPrivateTeam(
      conversation,
      currentUser,
      currentRole,
      myTeams,
      supervisedTeamIds
    )
  ) {
    return true;
  }

  return false;
};
